import { Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild, ChangeDetectionStrategy, ChangeDetectorRef, NgZone } from '@angular/core';
import { GoogleMapsService } from '../../services/google-maps.service';

@Component({
  selector: 'app-google-map',
  templateUrl: './google-map.component.html',
  styleUrls: ['./google-map.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class GoogleMapComponent implements OnInit, OnChanges, OnDestroy {
  @ViewChild('mapContainer', { static: true }) mapContainer!: ElementRef;
  
  @Input() latitude: number = 0;
  @Input() longitude: number = 0;
  @Input() zoom: number = 15;
  @Input() height: string = '400px';
  @Input() width: string = '100%';
  @Input() draggable: boolean = true;
  @Input() scrollwheel: boolean = true;
  @Input() disableDefaultUI: boolean = false;
  @Input() zoomControl: boolean = true;
  @Input() mapTypeControl: boolean = true;
  @Input() scaleControl: boolean = true;
  @Input() streetViewControl: boolean = true;
  @Input() rotateControl: boolean = true;
  @Input() fullscreenControl: boolean = true;
  @Input() styles: any[] = [];
  @Input() marker: boolean = true;
  @Input() markerDraggable: boolean = false;
  @Input() markerTitle: string = '';
  @Input() markerLabel: string = '';
  @Input() markerIcon: string = '';
  @Input() markerAnimation: string = '';
  @Input() selectionZoom: number = 18;

  @Output() mapClick = new EventEmitter<any>();
  @Output() markerClick = new EventEmitter<any>();
  @Output() markerDragEnd = new EventEmitter<any>();
  @Output() mapReady = new EventEmitter<any>();
  @Output() boundsChanged = new EventEmitter<any>();
  @Output() centerChanged = new EventEmitter<any>();
  @Output() zoomChanged = new EventEmitter<any>();
  @Output() locationSelected = new EventEmitter<{ lat: number; lng: number; address: string }>();

  private map: any;
  private markerInstance: any;
  private mapClickListener: any;
  private markerClickListener: any;
  private markerDragEndListener: any;
  private boundsChangedListener: any;
  private centerChangedListener: any;
  private zoomChangedListener: any;

  constructor(
    private googleMapsService: GoogleMapsService,
    private cdr: ChangeDetectorRef,
    private zone: NgZone
  ) {}

  async ngOnInit() {
    try {
      await this.googleMapsService.loadGoogleMaps();
      this.initializeMap();
    } catch (error) {
      console.error('Error initializing Google Map:', error);
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (!this.map) {
      return;
    }

    if (changes['latitude'] || changes['longitude']) {
      if (typeof this.latitude === 'number' && typeof this.longitude === 'number' && !isNaN(this.latitude) && !isNaN(this.longitude)) {
        this.setCenter(this.latitude, this.longitude);
        
        if (this.marker && !this.markerInstance) {
          this.addMarker();
        } else if (this.marker && this.markerInstance) {
          this.setMarkerPosition(this.latitude, this.longitude);
        }
      }
    }

    if (changes['zoom']) {
      this.setZoom(this.zoom);
    }

    if (changes['marker']) {
      if (this.marker && !this.markerInstance) {
        this.addMarker();
      } else if (!this.marker && this.markerInstance) {
        this.markerInstance.setMap(null);
        this.markerInstance = null;
      }
    }

    this.cdr.markForCheck();
  }

  ngOnDestroy() {
    this.removeListeners();
  }

  private initializeMap() {
    const google = this.googleMapsService.getGoogleMaps();
    
    const mapOptions = {
      center: { lat: this.latitude, lng: this.longitude },
      zoom: this.zoom,
      draggable: this.draggable,
      scrollwheel: this.scrollwheel,
      disableDefaultUI: this.disableDefaultUI,
      zoomControl: this.zoomControl,
      mapTypeControl: this.mapTypeControl,
      scaleControl: this.scaleControl,
      streetViewControl: this.streetViewControl,
      rotateControl: this.rotateControl,
      fullscreenControl: this.fullscreenControl,
      styles: this.styles
    };

    this.map = new google.maps.Map(this.mapContainer.nativeElement, mapOptions);
    
    if (this.marker) {
      this.addMarker();
    }

    this.addMapListeners();
    this.mapReady.emit(this.map);
  }

  private addMarker() {
    const google = this.googleMapsService.getGoogleMaps();
    
    const markerOptions: any = {
      position: { lat: this.latitude, lng: this.longitude },
      map: this.map,
      draggable: this.markerDraggable,
      title: this.markerTitle,
      label: this.markerLabel
    };

    if (this.markerIcon) {
      markerOptions.icon = this.markerIcon;
    }

    if (this.markerAnimation) {
      markerOptions.animation = google.maps.Animation[this.markerAnimation.toUpperCase()];
    }

    this.markerInstance = new google.maps.Marker(markerOptions);
    this.addMarkerListeners();
  }

  private addMapListeners() {
    this.mapClickListener = this.map.addListener('click', (event: any) => {
      const coords = {
        lat: event.latLng.lat(),
        lng: event.latLng.lng(),
        event: event
      };
      this.mapClick.emit(coords);
      this.selectLocation(coords.lat, coords.lng);
    });

    this.boundsChangedListener = this.map.addListener('bounds_changed', () => {
      this.boundsChanged.emit(this.map.getBounds());
    });

    this.centerChangedListener = this.map.addListener('center_changed', () => {
      const center = this.map.getCenter();
      this.centerChanged.emit({
        lat: center.lat(),
        lng: center.lng()
      });
    });

    this.zoomChangedListener = this.map.addListener('zoom_changed', () => {
      this.zoomChanged.emit(this.map.getZoom());
    });
  }

  private addMarkerListeners() {
    if (!this.markerInstance) return;

    this.markerClickListener = this.markerInstance.addListener('click', (event: any) => {
      this.markerClick.emit({
        lat: this.markerInstance.getPosition().lat(),
        lng: this.markerInstance.getPosition().lng(),
        event: event
      });
    });

    if (this.markerDraggable) {
      this.markerDragEndListener = this.markerInstance.addListener('dragend', (event: any) => {
        const lat = this.markerInstance.getPosition().lat();
        const lng = this.markerInstance.getPosition().lng();
        this.markerDragEnd.emit({ lat, lng, event });
        this.selectLocation(lat, lng);
      });
    }
  }

  private removeListeners() {
    const google = this.googleMapsService.getGoogleMaps();

    if (this.mapClickListener) {
      google.maps.event.removeListener(this.mapClickListener);
    }
    if (this.markerClickListener) {
      google.maps.event.removeListener(this.markerClickListener);
    }
    if (this.markerDragEndListener) {
      google.maps.event.removeListener(this.markerDragEndListener);
    }
    if (this.boundsChangedListener) {
      google.maps.event.removeListener(this.boundsChangedListener);
    }
    if (this.centerChangedListener) {
      google.maps.event.removeListener(this.centerChangedListener);
    }
    if (this.zoomChangedListener) {
      google.maps.event.removeListener(this.zoomChangedListener);
    }
  }

  // Métodos públicos para interactuar con el mapa
  public setCenter(lat: number, lng: number) {
    if (this.map && typeof lat === 'number' && typeof lng === 'number' && !isNaN(lat) && !isNaN(lng)) {
      this.map.setCenter({ lat, lng });
    }
  }

  public setZoom(zoom: number) {
    if (this.map) {
      this.map.setZoom(zoom);
    }
  }

  public setMarkerPosition(lat: number, lng: number) {
    if (this.markerInstance && typeof lat === 'number' && typeof lng === 'number' && !isNaN(lat) && !isNaN(lng)) {
      this.markerInstance.setPosition({ lat, lng });
    } else {
      if (this.marker) this.addMarker();
    }
  }

  public fitBounds(bounds: any) {
    if (this.map) {
      this.map.fitBounds(bounds);
    }
  }

  public panTo(lat: number, lng: number) {
    if (this.map) {
      this.map.panTo({ lat, lng });
    }
  }

  public getMap(): any {
    return this.map;
  }

  public getMarker(): any {
    return this.markerInstance;
  }

  public updateMapPosition(lat: number, lng: number, zoom?: number, showMarker: boolean = true): void {
    if (!this.map) {
      return;
    }

    this.latitude = lat;
    this.longitude = lng;
    if (zoom) this.zoom = zoom;
    this.marker = showMarker;

    this.setCenter(lat, lng);
    if (zoom) this.setZoom(zoom);
    
    if (showMarker) {
      if (!this.markerInstance) {
        this.addMarker();
      } else {
        this.setMarkerPosition(lat, lng);
      }
    } else if (this.markerInstance) {
      this.markerInstance.setMap(null);
      this.markerInstance = null;
    }
  }

  public async selectLocation(lat: number, lng: number): Promise<void> {
    try {
      const targetZoom = this.selectionZoom || this.zoom;
      this.updateMapPosition(lat, lng, targetZoom, true);

      const address = await this.googleMapsService.reverseGeocode({ lat, lng });
      this.zone.run(() => this.locationSelected.emit({ lat, lng, address }));
    } catch (error) {
      console.error('Error in selectLocation:', error);
    }
  }
} 